import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { adminApi, ActivityLeaderboardReport, Organization, UserLeaderboardRow, OrgLeaderboardRow } from '../services/api';
import PageHelp from '../components/PageHelp';
import { PAGE_HELP } from '../components/page-help-content';
import { RANGE_PRESETS, RangePreset, resolveRange, formatRangeLabel } from '../utils/dateRange';
import { IS_BROKER_ADMIN } from '../config/admin-build';

// Cap the ranked tables + chart so a long tail doesn't dominate; the headline is
// who's at the top.
const TOP_N = 15;
const MAX_CHART_BARS = 10;

// The broker-admin build (IS_BROKER_ADMIN, from config/admin-build) is scoped to a
// single org, so the Organisation filter is redundant + would only enumerate one
// option. Hidden there (legal-ai-admin/broker-admin-visibility-on-new-surface.md);
// the date-range filter still applies on both builds.

function userDisplayName(row: UserLeaderboardRow): string {
  const name = `${row.firstName || ''} ${row.lastName || ''}`.trim();
  return name || row.email || row.userId;
}

function lastRunLabel(row: UserLeaderboardRow): string {
  if (row.daysSinceLastRun === null) return 'Never';
  if (row.daysSinceLastRun === 0) return 'Today';
  return `${row.daysSinceLastRun}d ago`;
}

const LeaderboardPage: React.FC = () => {
  // Date-range + org filter (#514). Default to "Today" — the literal ask on the
  // ticket ("top 10 users for the day"). '' organizationId = all organisations.
  const [preset, setPreset] = useState<RangePreset>('today');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [organizationId, setOrganizationId] = useState<string>('');
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [report, setReport] = useState<ActivityLeaderboardReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Resolve the chosen preset (or custom inputs) into a UTC ISO window. Null =
  // custom range not yet fully specified (we then hold off fetching).
  const range = useMemo(
    () => resolveRange(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  // Org list for the filter dropdown — fetched once; non-fatal if it fails
  // (the dropdown just shows "All organisations"). Skipped entirely on the
  // broker-admin build, where the org filter is hidden.
  useEffect(() => {
    if (IS_BROKER_ADMIN) return;
    let cancelled = false;
    adminApi
      .listOrganizations()
      .then((res: any) => {
        if (!cancelled && res?.success) setOrgs(res.data?.organizations ?? []);
      })
      .catch(() => {
        /* non-fatal — leaderboard still works without the org dropdown */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!range) {
      // Custom range incomplete — wait for both dates before fetching.
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminApi
      .getActivityLeaderboard({ from: range.from, to: range.to, organizationId: organizationId || undefined })
      .then((response) => {
        if (cancelled) return;
        if (response.success) {
          setReport(response.data);
        } else {
          setError('Failed to load activity leaderboard.');
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load activity leaderboard.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, organizationId]);

  const summary = report?.summary;
  const orgOptions = useMemo(
    () => [...orgs].sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id)),
    [orgs],
  );

  const cards: { key: string; caption: string; value: string }[] = [
    { key: 'runs', caption: 'Total runs', value: (summary?.totalRuns ?? 0).toLocaleString() },
    { key: 'activeUsers', caption: 'Active users', value: (summary?.activeUsers ?? 0).toLocaleString() },
    { key: 'activeOrgs', caption: 'Active orgs', value: (summary?.activeOrgs ?? 0).toLocaleString() },
    { key: 'topUser', caption: 'Top user', value: summary?.topUserEmail || '—' },
  ];

  const topUsers = useMemo(() => (report?.topUsers ?? []).slice(0, TOP_N), [report]);
  const topOrgs = useMemo(() => (report?.topOrgs ?? []).slice(0, TOP_N), [report]);

  const orgChartData = useMemo(
    () =>
      (report?.topOrgs ?? [])
        .filter((o) => o.runs > 0)
        .slice(0, MAX_CHART_BARS)
        .map((o) => ({ name: o.organizationName || o.organizationId, runs: o.runs })),
    [report],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'flex-start' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Activity Leaderboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The most-active users and organisations on your stack for the selected window, by runs
            (~200k tokens each). Filter by date range and organisation to see, e.g., the top users
            today or a single company's power users.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
          sx={{ flexWrap: 'wrap', gap: 1.5 }}
        >
          <TextField
            select
            label="Date range"
            size="small"
            value={preset}
            onChange={(e) => setPreset(e.target.value as RangePreset)}
            sx={{ minWidth: 150 }}
            inputProps={{ 'data-testid': 'preset-select' }}
          >
            {RANGE_PRESETS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          {preset === 'custom' && (
            <>
              <TextField
                type="date"
                label="From"
                size="small"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'custom-from' }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                type="date"
                label="To"
                size="small"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'custom-to' }}
                sx={{ minWidth: 150 }}
              />
            </>
          )}
          {!IS_BROKER_ADMIN && (
            <TextField
              select
              label="Organisation"
              size="small"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              sx={{ minWidth: 200 }}
              inputProps={{ 'data-testid': 'org-select' }}
            >
              <MenuItem value="">All organisations</MenuItem>
              {orgOptions.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name || o.id}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </Stack>

      <PageHelp {...PAGE_HELP.leaderboard} />

      {preset === 'custom' && !range && (
        <Alert severity="info" sx={{ mb: 3 }} data-testid="custom-range-hint">
          Pick both a start and end date to load the leaderboard.
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && report && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} data-testid="summary-cards">
            {cards.map((card) => (
              <Card key={card.key} sx={{ flex: 1 }} data-testid={`card-${card.key}`}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    {card.caption}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1, wordBreak: 'break-word' }} data-testid={`card-${card.key}-value`}>
                    {card.value}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {orgChartData.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }} data-testid="org-chart">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Top organisations by runs
              </Typography>
              <ResponsiveContainer width="100%" height={Math.max(160, orgChartData.length * 34)}>
                <BarChart data={orgChartData} layout="vertical" margin={{ left: 24, right: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 12 }} />
                  <RechartsTooltip />
                  <Bar dataKey="runs" name="Runs" fill="#3182ce" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            {/* Top users */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Top users
              </Typography>
              <Paper variant="outlined">
                <TableContainer>
                  <Table size="small" data-testid="user-table">
                    <TableHead sx={{ bgcolor: 'action.hover' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Runs</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Last run</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topUsers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No user activity on this stack for the selected window.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {topUsers.map((row, i) => (
                        <TableRow key={row.userId} hover data-testid={`user-row-${row.userId}`}>
                          <TableCell>
                            {i === 0 && row.runs > 0 ? (
                              <Chip size="small" color="success" variant="outlined" label="1" />
                            ) : (
                              i + 1
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {userDisplayName(row)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.organizationName || row.organizationId || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                            {row.runs.toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Tooltip title={row.lastRunAt ? new Date(row.lastRunAt).toLocaleString() : 'Never'}>
                              <Typography variant="body2">{lastRunLabel(row)}</Typography>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>

            {/* Top orgs */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Top organisations
              </Typography>
              <Paper variant="outlined">
                <TableContainer>
                  <Table size="small" data-testid="org-table">
                    <TableHead sx={{ bgcolor: 'action.hover' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Organisation</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Runs</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Active / members</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topOrgs.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No organisation activity on this stack for the selected window.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {topOrgs.map((row, i) => (
                        <TableRow key={row.organizationId} hover data-testid={`org-row-${row.organizationId}`}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {row.organizationName || row.organizationId}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                            {row.runs.toLocaleString()}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            {row.activeMembers.toLocaleString()} / {row.memberCount.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }} data-testid="leaderboard-caption">
            {formatRangeLabel(preset, range)}
            {organizationId
              ? ` · ${orgs.find((o) => o.id === organizationId)?.name || 'selected organisation'}`
              : ' · all organisations'}
            {' · '}top {topUsers.length} of {(report.totalUsers ?? topUsers.length).toLocaleString()} users
            {' · '}top {topOrgs.length} of {(report.totalOrgs ?? topOrgs.length).toLocaleString()} orgs
            {' · '}generated {new Date(report.generatedAt).toLocaleString()}
            {report.deferred?.length ? ` · not yet included: ${report.deferred.join('; ')}` : ''}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default LeaderboardPage;
